import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEC } from './SecurityTheme';
import {
  listInvestigations,
  InvestigationItem,
} from '../../services/api';
import { format, parseISO } from 'date-fns';
import {
  Search,
  Plus,
  Loader2,
  RefreshCw,
  FileSearch,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Beaker,
  Activity,
} from 'lucide-react';

const STATUS_ICONS: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  pending: Clock,
  collecting: Activity,
  analyzing: Beaker,
  complete: CheckCircle2,
  failed: XCircle,
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  collecting: 'Collecting',
  analyzing: 'Analyzing',
  complete: 'Complete',
  failed: 'Failed',
};

const TEMPLATE_LABELS: Record<string, string> = {
  bec: 'BEC Investigation',
  account_takeover: 'Account Takeover',
  phishing_response: 'Phishing Response',
  insider_threat: 'Insider Threat',
  post_breach: 'Post-Breach Cleanup',
  health_check: 'Quick Health Check',
};

function investigationStatusColor(status: string): string {
  switch (status) {
    case 'pending': return SEC.t2;
    case 'collecting': return SEC.cyan;
    case 'analyzing': return SEC.amber;
    case 'complete': return SEC.green;
    case 'failed': return SEC.red;
    default: return SEC.t3;
  }
}

export default function InvestigationsPage() {
  const navigate = useNavigate();
  const [investigations, setInvestigations] = useState<InvestigationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showWizard, setShowWizard] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const resp = await listInvestigations();
      if (resp.success) {
        setInvestigations(resp.investigations);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load investigations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Filter investigations
  const filtered = investigations.filter(inv => {
    if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        inv.title.toLowerCase().includes(q) ||
        inv.clientName.toLowerCase().includes(q) ||
        inv.tenantDomain.toLowerCase().includes(q) ||
        (inv.templateId && TEMPLATE_LABELS[inv.templateId]?.toLowerCase().includes(q))
      );
    }
    return true;
  });

  // Status counts
  const statusCounts = {
    all: investigations.length,
    pending: investigations.filter(i => i.status === 'pending').length,
    collecting: investigations.filter(i => i.status === 'collecting').length,
    analyzing: investigations.filter(i => i.status === 'analyzing').length,
    complete: investigations.filter(i => i.status === 'complete').length,
    failed: investigations.filter(i => i.status === 'failed').length,
  };

  return (
    <div style={{ minHeight: '100vh', background: SEC.bg, color: SEC.t1, padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <FileSearch style={{ color: SEC.cyan }} size={24} />
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: SEC.t1, margin: 0 }}>Investigations</h1>
            <p style={{ fontSize: 13, color: SEC.t2, margin: 0 }}>Security investigation management and forensic analysis</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6,
              background: SEC.panel2, border: `1px solid ${SEC.border}`,
              color: SEC.t2, cursor: 'pointer', fontSize: 13,
            }}
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => setShowWizard(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 6,
              background: SEC.cyan, border: 'none',
              color: '#000', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}
          >
            <Plus size={14} />
            New Investigation
          </button>
        </div>
      </div>

      {/* Status tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, overflowX: 'auto' }}>
        {(['all', 'pending', 'collecting', 'analyzing', 'complete', 'failed'] as const).map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: '6px 14px', borderRadius: 6, border: 'none',
              background: statusFilter === s ? SEC.cyanGlow : SEC.panel,
              color: statusFilter === s ? SEC.cyan : SEC.t2,
              cursor: 'pointer', fontSize: 12, fontWeight: 500,
              whiteSpace: 'nowrap',
              borderBottom: statusFilter === s ? `2px solid ${SEC.cyan}` : '2px solid transparent',
            }}
          >
            {s === 'all' ? 'All' : STATUS_LABELS[s]} ({statusCounts[s]})
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: SEC.t3 }} />
        <input
          type="text"
          placeholder="Search investigations..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            width: '100%', padding: '10px 12px 10px 34px',
            background: SEC.panel, border: `1px solid ${SEC.border}`,
            borderRadius: 8, color: SEC.t1, fontSize: 13,
            outline: 'none',
          }}
        />
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 16,
          background: 'rgba(239,68,68,0.1)', border: `1px solid ${SEC.red}`,
          color: SEC.red, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: SEC.t2 }}>
          <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 12px' }} />
          Loading investigations...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 60, color: SEC.t3,
          background: SEC.panel, borderRadius: 8, border: `1px solid ${SEC.border}`,
        }}>
          <FileSearch size={40} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
          <p style={{ fontSize: 15, marginBottom: 4 }}>No investigations found</p>
          <p style={{ fontSize: 13, color: SEC.t3 }}>
            {searchQuery || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first investigation to get started'}
          </p>
        </div>
      ) : (
        /* Investigations table */
        <div style={{
          background: SEC.panel, border: `1px solid ${SEC.border}`,
          borderRadius: 8, overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: SEC.header }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', color: SEC.cyan, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', color: SEC.cyan, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Title</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', color: SEC.cyan, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Client</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', color: SEC.cyan, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Template</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', color: SEC.cyan, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Users</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', color: SEC.cyan, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date Range</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', color: SEC.cyan, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', color: SEC.cyan, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => {
                const StatusIcon = STATUS_ICONS[inv.status] || Clock;
                const sColor = investigationStatusColor(inv.status);
                return (
                  <tr
                    key={inv.id}
                    onClick={() => navigate(`/security/investigations/${inv.id}?clientId=${inv.clientId}`)}
                    style={{
                      borderBottom: `1px solid ${SEC.border}`,
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = SEC.panel2)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '3px 10px', borderRadius: 9999,
                        background: `${sColor}18`, color: sColor,
                        fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                      }}>
                        <StatusIcon style={{ width: 12, height: 12 }} />
                        {STATUS_LABELS[inv.status] || inv.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 500, color: SEC.t1 }}>
                      {inv.title}
                    </td>
                    <td style={{ padding: '10px 14px', color: SEC.t2 }}>
                      {inv.clientName}
                    </td>
                    <td style={{ padding: '10px 14px', color: SEC.t2 }}>
                      {inv.templateId ? TEMPLATE_LABELS[inv.templateId] || inv.templateId : 'Custom'}
                    </td>
                    <td style={{ padding: '10px 14px', color: SEC.t2 }}>
                      {inv.targetUsers.length}
                    </td>
                    <td style={{ padding: '10px 14px', color: SEC.t2, whiteSpace: 'nowrap' }}>
                      {format(parseISO(inv.dateRangeStart), 'MMM d')} - {format(parseISO(inv.dateRangeEnd), 'MMM d, yyyy')}
                    </td>
                    <td style={{ padding: '10px 14px', color: SEC.t3, whiteSpace: 'nowrap', fontSize: 12 }}>
                      {format(parseISO(inv.createdDate), 'MMM d, h:mm a')}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <ChevronRight size={14} style={{ color: SEC.t3 }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Wizard modal */}
      {showWizard && (
        <InvestigationWizardModal onClose={() => setShowWizard(false)} onCreated={() => { setShowWizard(false); fetchData(); }} />
      )}
    </div>
  );
}

// ============================================
// Inline Wizard Modal (imported dynamically)
// ============================================
import InvestigationWizard from './InvestigationWizard';

function InvestigationWizardModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        }}
      />
      {/* Modal */}
      <div style={{
        position: 'relative', zIndex: 1,
        width: '90%', maxWidth: 800, maxHeight: '90vh',
        background: SEC.panel, border: `1px solid ${SEC.border}`,
        borderRadius: 12, overflow: 'auto',
      }}>
        <InvestigationWizard onClose={onClose} onCreated={onCreated} />
      </div>
    </div>
  );
}
